import { useEffect, useRef, useState } from "react";
import { CheckComponentType } from "winslow-core";
import {
  useCheckComponent,
  UseCheckComponentConfig,
} from "./hooks/useCheckComponent";
import { useCheckSDK } from "./hooks/useCheckSDK";
import { CheckComponentHandler } from "../../types/check-sdk";
import "./CheckEmbeddedComponent.css";

const DEFAULT_CHECK_APPEARANCE: Record<string, any> = { 
  includeCloseButton: false,
  includeLogo: true,
  primaryColor: "#FFD3AC",
  fontFamily: 'Inter, sans-serif',
    // monospaceFontFamily: 'Courier New, monospace',
  // minHeight: "600px",
};

export type CheckEmbeddedComponentV2Props = {
    url: string;
    title?: string;
    minHeight?: string;
    onEvent?: (event: string, data: any) => void;
    appearance?: Record<string, any>;
    divId?: string;
};

export const CheckEmbeddedComponentV2   = ({
    url,
    title = "CheckHQ Component",
    minHeight = "600px",
    onEvent,
    appearance = DEFAULT_CHECK_APPEARANCE,
    divId,
}: CheckEmbeddedComponentV2Props) => {
  
  const { sdkLoaded, sdkError } = useCheckSDK();
  const handlerRef = useRef<CheckComponentHandler | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [componentError, setComponentError] = useState<string | null>(null);

  useEffect(() => {
    if (!url || !sdkLoaded || !containerRef.current) {
      return;
    }

    console.log("[CheckEmbeddedComponent] Initializing CheckHQ component", {
      url,
      divId,
    });

    try {
      // alert("URL: " + url);
      // @ts-ignore - CheckComponent added at runtime by SDK
      const handler = window.CheckComponent.create({
        link: url,
        onEvent: (event: string, data: any) => {
          console.log("[CheckEmbeddedComponent] Event received:", event, data);
          // Log all events to help debug close button behavior
          if (event.toLowerCase().includes("close") || event.toLowerCase().includes("dismiss")) {
            console.log("[CheckEmbeddedComponent] Close-related event detected:", event, data);
          }
          if (onEvent) {
            onEvent(event, data);
          }
        },
        onClose: () => {
          console.log("[CheckEmbeddedComponent] Onboarding form closed");
          if (onEvent) {
            onEvent("component-closed", { reason: "user-action" });
          }
        },
        appearance,
        divId,
        allowProcessingPeriodEdits: false,
        allowPaydayEdits: false,
      });

      console.log("[CheckEmbeddedComponent] Handler created:", handler);

      handlerRef.current = handler;
      handler.open();
      // handler.close = () => {
      //   console.log("[CheckEmbeddedComponent] Onboarding form closed overridden");
      //   // if (onEvent) {
      //   //   onEvent("component-closed", { reason: "user-action" });
      //   // }
      // };

      console.log("[CheckEmbeddedComponent] Component opened successfully");

      const embeddedDivId = divId || "check-component-embedded-div";
      let wasVisible = false;

      const checkInterval = setInterval(() => {
        const embeddedDiv = document.getElementById(embeddedDivId);
        const isVisible = embeddedDiv && containerRef.current && containerRef.current.contains(embeddedDiv);
        
        if (embeddedDiv && containerRef.current && !containerRef.current.contains(embeddedDiv)) {
          containerRef.current.appendChild(embeddedDiv);
          clearInterval(checkInterval);
          console.log("[CheckEmbeddedComponent] Component div attached to container");
          wasVisible = true;
        } else if (wasVisible && embeddedDiv && !isVisible) {
          // Component was visible but is now hidden/removed - likely closed
          console.log("[CheckEmbeddedComponent] Component appears to have been closed");
          if (onEvent) {
            onEvent("component-closed", { reason: "user-action" });
          }
          clearInterval(checkInterval);
        } else if (embeddedDiv && isVisible) {
          wasVisible = true;
        }
      }, 100);

      setTimeout(() => clearInterval(checkInterval), 5000);

      // Also listen for DOM mutations to detect when the component is removed
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (element.id === embeddedDivId || element.querySelector(`#${embeddedDivId}`)) {
                console.log("[CheckEmbeddedComponent] Component removed from DOM");
                if (onEvent) {
                  onEvent("component-closed", { reason: "dom-removed" });
                }
              }
            }
          });
        });
      });

      if (containerRef.current) {
        observer.observe(containerRef.current, {
          childList: true,
          subtree: true,
        });
      }

      return () => {
        console.log("[CheckEmbeddedComponent] Cleaning up component");
        observer.disconnect();
        clearInterval(checkInterval);
        if (handlerRef.current) {
          handlerRef.current.close();
          handlerRef.current = null;
        }
      };
    } catch (err) {
      console.error("[CheckEmbeddedComponent] Error initializing component:", err);
      setComponentError(
        err instanceof Error ? err.message : "Failed to initialize component"
      );
    }
  }, [
    url,
    sdkLoaded,
    onEvent,
    appearance,
    divId,
  ]);

  const loading = !sdkLoaded;
  const error = sdkError?.message || componentError;

  if (loading) {
    return (
      <div className="check-embedded-component check-loading">
        <div className="loading-spinner"></div>
        <h3>Loading Onboarding Form...</h3>
      </div>
    );
  }

  if (error) {
    return (
      <div className="check-embedded-component check-error">
        <div className="error-icon">⚠️</div>
        <h3>Error Loading Onboarding</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (!url) {
    return (
      <div className="check-embedded-component check-error">
        <div className="error-icon">⚠️</div>
        <h3>No URL Available</h3>
        <p>Unable to load the onboarding form at this time.</p>
      </div>
    );
  }

  console.log("[CheckEmbeddedComponent] SDK error:", sdkError);
  console.log("[CheckEmbeddedComponent] Component error:", componentError);

  return (
    <div
      ref={containerRef}
      className="check-embedded-component"
      style={{ minHeight, height: "100%", width: "100%" }}
      aria-label={title}
    />
  );
};

